/**
 * Live-view map plumbing — owns the drone-marker source/layers that the
 * operator's real-time telemetry will eventually populate via webhook.
 *
 * Scope after the demo strip:
 *   - Mounts an empty drone-marker source + layer stack while the live view
 *     is open (so real telemetry can populate it via map.getSource(...).setData
 *     without re-creating the layers each tick).
 *   - Tears the source/layers down when the live view closes.
 *
 * Synthesizing telemetry from the planned mission was deliberately removed
 * along with the demo's playback controls — real flights produce real
 * telemetry, no need to fake it.
 *
 * Isolation contract:
 *   - All sources/layers prefixed `dock-demo-live-*`
 *   - Existence-checked addSource/addLayer (idempotent)
 *   - styledata listener re-pins on every event so MapCanvas-added platform
 *     layers don't bury the drone marker
 *   - Re-installs on `style.load`
 *   - Teardown on unmount removes only what the hook owns
 */

import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';

const SRC_DRONE = 'dock-demo-live-drone-src';
const LAYER_DRONE_HALO = 'dock-demo-live-drone-halo-lyr';
const LAYER_DRONE_BODY = 'dock-demo-live-drone-body-lyr';
const LAYER_DRONE_HEADING = 'dock-demo-live-drone-heading-lyr';
const LAYER_DRONE_GIMBAL = 'dock-demo-live-drone-gimbal-lyr';

const ALL_LAYERS = [
  LAYER_DRONE_HALO,
  LAYER_DRONE_BODY,
  LAYER_DRONE_HEADING,
  LAYER_DRONE_GIMBAL,
] as const;

function ensureSourcesAndLayers(map: MapboxMap): void {
  if (!map.getSource(SRC_DRONE)) {
    map.addSource(SRC_DRONE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  if (!map.getLayer(LAYER_DRONE_HALO)) {
    map.addLayer({
      id: LAYER_DRONE_HALO,
      type: 'circle',
      source: SRC_DRONE,
      paint: {
        'circle-radius': 24,
        'circle-color': '#22d3ee',
        'circle-opacity': 0.18,
        'circle-stroke-width': 0,
      },
    });
  }
  if (!map.getLayer(LAYER_DRONE_BODY)) {
    map.addLayer({
      id: LAYER_DRONE_BODY,
      type: 'circle',
      source: SRC_DRONE,
      paint: {
        'circle-radius': 11,
        'circle-color': '#22d3ee',
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#ffffff',
      },
    });
  }
  if (!map.getLayer(LAYER_DRONE_HEADING)) {
    // Heading arrow — rotates to track the drone's bearing once real
    // telemetry populates the source. text-rotate = heading - 90 because
    // the ➤ glyph naturally points east.
    map.addLayer({
      id: LAYER_DRONE_HEADING,
      type: 'symbol',
      source: SRC_DRONE,
      layout: {
        'text-field': '➤',
        'text-size': 18,
        'text-rotate': ['-', ['coalesce', ['get', 'heading'], 0], 90],
        'text-rotation-alignment': 'map',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-offset': [1.4, 0],
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(8, 145, 178, 0.95)',
        'text-halo-width': 1.5,
      },
    });
  }
  if (!map.getLayer(LAYER_DRONE_GIMBAL)) {
    map.addLayer({
      id: LAYER_DRONE_GIMBAL,
      type: 'symbol',
      source: SRC_DRONE,
      layout: {
        'text-field': '▸',
        'text-size': 22,
        'text-rotate': ['-', ['coalesce', ['get', 'gimbalYaw'], 0], 90],
        'text-rotation-alignment': 'map',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-offset': [1.9, 0],
      },
      paint: {
        'text-color': '#fbbf24',
        'text-halo-color': 'rgba(0, 0, 0, 0.85)',
        'text-halo-width': 1.4,
      },
    });
  }
}

function pinLayersToTop(map: MapboxMap): void {
  for (const id of ALL_LAYERS) {
    try {
      if (map.getLayer(id)) map.moveLayer(id);
    } catch {
      // ignore
    }
  }
}

function teardownSourcesAndLayers(map: MapboxMap): void {
  for (const id of [...ALL_LAYERS].reverse()) {
    try {
      if (map.getLayer(id)) map.removeLayer(id);
    } catch {
      // map may be in teardown; defensive
    }
  }
  try {
    if (map.getSource(SRC_DRONE)) map.removeSource(SRC_DRONE);
  } catch {
    // ignore
  }
}

/**
 * Owns the drone-marker source/layer lifecycle while the live view is open.
 * The source is created empty; a real-telemetry consumer would push features
 * into it via map.getSource(SRC_DRONE)?.setData(...) on each webhook tick.
 *
 * Returns nothing — the previous synthesized-state return value was removed
 * along with the demo simulator. Callers that need to know whether the live
 * view is open should use `useIsLiveViewOpen()` instead.
 */
export function useDemoLiveView(): void {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const isOpen = useIsLiveViewOpen();

  useEffect(() => {
    if (!map || !isMapReady || !isOpen) return;

    const reinstall = () => {
      ensureSourcesAndLayers(map);
      pinLayersToTop(map);
    };
    const repin = () => pinLayersToTop(map);

    if (map.isStyleLoaded()) reinstall();
    map.on('style.load', reinstall);
    map.on('styledata', repin);

    return () => {
      map.off('style.load', reinstall);
      map.off('styledata', repin);
      teardownSourcesAndLayers(map);
    };
  }, [map, isMapReady, isOpen]);
}

/**
 * Synchronous check for "live view is renderable" — the EditorPage mount
 * point uses this to decide whether to render the HUD at all.
 */
export function useIsLiveViewOpen(): boolean {
  const liveViewMissionId = useDockDemoStore((s) => s.liveViewMissionId);
  const missions = useDockDemoStore((s) => s.missions);
  const dockStations = useDockDemoStore((s) => s.dockStations);
  const m = liveViewMissionId ? missions.find((x) => x.id === liveViewMissionId) : null;
  const d = m?.dockStationId ? dockStations.find((x) => x.id === m.dockStationId) : null;
  return !!m && !!d;
}
